import { FEATURE_FLAG_PORTAL_CHANGE_TRACKING } from '@changeTracking/pages/featureFlagPortal/sources';
import type {
  ChangeTrackingPageRegistry,
  ChangeTrackingPageSourceIds,
  ChangeTrackingPageSourceIdsMap,
  ChangeTrackingPageSources,
  ChangeTrackingPageSourcesMap,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';

import { ADMIN_TOOLS_CHANGE_TRACKING } from '../pages/adminTools/sources';
import { CAPACITY_FINDER_CHANGE_TRACKING } from '../pages/capacityFinder/sources';
import { CARRIER_BLACKLIST_REPORT_CHANGE_TRACKING } from '../pages/carrierBlacklistReport/sources';
import { CARRIER_DETAILS_CHANGE_TRACKING } from '../pages/carrierDetails/sources';
import { CARRIER_MATCHING_CHANGE_TRACKING } from '../pages/carrierMatching/sources';
import { CARRIER_PLAYLISTS_CHANGE_TRACKING } from '../pages/carrierPlaylists/sources';
import { CARRIER_CHANGE_TRACKING } from '../pages/carriers/sources';
import { CLAIM_FORM_CHANGE_TRACKING } from '../pages/claimForm/sources';
import { COLLECTIONS_CHANGE_TRACKING } from '../pages/collections/sources';
import { COLLECTIONS_BY_CUSTOMER_CHANGE_TRACKING } from '../pages/collectionsByCustomer/sources';
import { CUSTOMER_CREATE_CHANGE_TRACKING } from '../pages/customerCreate/sources';
import { CUSTOMER_DETAILS_CHANGE_TRACKING } from '../pages/customerDetails/sources';
import { DORMANT_CUSTOMERS_CHANGE_TRACKING } from '../pages/dormantCustomers/sources';
import { FACTORING_COMPANY_DETAILS_CHANGE_TRACKING } from '../pages/factoringCompanyDetails/sources';
import { FEATURE_FLAGS_CHANGE_TRACKING } from '../pages/featureFlags/sources';
import { LOADBOARD_CHANGE_TRACKING } from '../pages/loadboard/sources';
import { LOAD_SEARCH_CHANGE_TRACKING } from '../pages/loadSearch/sources';
import { LOAD_TENDERS_CHANGE_TRACKING } from '../pages/loadTenders/sources';
import { OFFICE_DETAILS_CHANGE_TRACKING } from '../pages/officeDetails/sources';
import { ORDER_DETAILS_CHANGE_TRACKING } from '../pages/orderDetails/sources';
import { POSTING_CHANGE_TRACKING } from '../pages/posting/sources';
import { PROSPECT_CREATE_CHANGE_TRACKING } from '../pages/prospectCreate/sources';
import { PROSPECT_DETAILS_CHANGE_TRACKING } from '../pages/prospectDetails/sources';
import { PROSPECTS_CHANGE_TRACKING } from '../pages/prospects/sources';
import { REVENUE_DASHBOARD_CHANGE_TRACKING } from '../pages/revenueDashboard/sources';
import { TRACK_TRACE_CHANGE_TRACKING } from '../pages/trackTrace/sources';
import { USER_DETAILS_CHANGE_TRACKING } from '../pages/userDetails/sources';
import { VENDOR_DETAILS_CHANGE_TRACKING } from '../pages/vendorDetails/sources';
import { VENDORS_CHANGE_TRACKING } from '../pages/vendors/sources';

const getPageSources = <TRegistry extends ChangeTrackingPageRegistry>(
  registry: TRegistry,
) =>
  Object.fromEntries(
    Object.entries(registry).map(([key, value]) => [key, value.source]),
  ) as ChangeTrackingPageSources<TRegistry>;

const getPageSourceIds = <TRegistry extends ChangeTrackingPageRegistry>(
  registry: TRegistry,
) =>
  Object.fromEntries(
    Object.values(registry).map((value) => [value.source, value.id]),
  ) as ChangeTrackingPageSourceIds<TRegistry>;

const getAllPageSources = <
  TRegistries extends Record<string, ChangeTrackingPageRegistry>,
>(
  registries: TRegistries,
) =>
  Object.fromEntries(
    Object.entries(registries).map(([page, registry]) => [
      page,
      getPageSources(registry),
    ]),
  ) as ChangeTrackingPageSourcesMap<TRegistries>;

const getAllPageSourceIds = <
  TRegistries extends Record<string, ChangeTrackingPageRegistry>,
>(
  registries: TRegistries,
) =>
  Object.assign(
    {},
    ...Object.values(registries).map((registry) => getPageSourceIds(registry)),
  ) as ChangeTrackingPageSourceIdsMap<TRegistries>;

// Add each new page registry here.
const CHANGE_TRACKING_PAGE_REGISTRIES = {
  adminTools: ADMIN_TOOLS_CHANGE_TRACKING,
  carriers: CARRIER_CHANGE_TRACKING,
  carrierBlacklistReport: CARRIER_BLACKLIST_REPORT_CHANGE_TRACKING,
  carrierDetails: CARRIER_DETAILS_CHANGE_TRACKING,
  carrierPlaylists: CARRIER_PLAYLISTS_CHANGE_TRACKING,
  carrierMatching: CARRIER_MATCHING_CHANGE_TRACKING,
  claimForm: CLAIM_FORM_CHANGE_TRACKING,
  collections: COLLECTIONS_CHANGE_TRACKING,
  collectionsByCustomer: COLLECTIONS_BY_CUSTOMER_CHANGE_TRACKING,
  customerCreate: CUSTOMER_CREATE_CHANGE_TRACKING,
  customerDetails: CUSTOMER_DETAILS_CHANGE_TRACKING,
  dormantCustomers: DORMANT_CUSTOMERS_CHANGE_TRACKING,
  capacityFinder: CAPACITY_FINDER_CHANGE_TRACKING,
  factoringCompanyDetails: FACTORING_COMPANY_DETAILS_CHANGE_TRACKING,
  featureFlags: FEATURE_FLAGS_CHANGE_TRACKING,
  featureFlagPortal: FEATURE_FLAG_PORTAL_CHANGE_TRACKING,
  loadboard: LOADBOARD_CHANGE_TRACKING,
  loadSearch: LOAD_SEARCH_CHANGE_TRACKING,
  loadTenders: LOAD_TENDERS_CHANGE_TRACKING,
  officeDetails: OFFICE_DETAILS_CHANGE_TRACKING,
  orderDetails: ORDER_DETAILS_CHANGE_TRACKING,
  posting: POSTING_CHANGE_TRACKING,
  prospectCreate: PROSPECT_CREATE_CHANGE_TRACKING,
  prospectDetails: PROSPECT_DETAILS_CHANGE_TRACKING,
  prospects: PROSPECTS_CHANGE_TRACKING,
  revenueDashboard: REVENUE_DASHBOARD_CHANGE_TRACKING,
  trackTrace: TRACK_TRACE_CHANGE_TRACKING,
  userDetails: USER_DETAILS_CHANGE_TRACKING,
  vendors: VENDORS_CHANGE_TRACKING,
  vendorDetails: VENDOR_DETAILS_CHANGE_TRACKING,
} as const;

export const CHANGE_TRACKING_SOURCES = getAllPageSources(
  CHANGE_TRACKING_PAGE_REGISTRIES,
);

export const CHANGE_TRACKING_SOURCE_IDS = getAllPageSourceIds(
  CHANGE_TRACKING_PAGE_REGISTRIES,
) satisfies Record<ChangeTrackingSource, number>;

const assertUniqueSourceIds = () => {
  const sourcesById = new Map<number, string>();

  Object.entries(CHANGE_TRACKING_SOURCE_IDS).forEach(([source, sourceId]) => {
    const existingSource = sourcesById.get(sourceId);

    if (existingSource !== undefined) {
      throw new Error(
        `Duplicate change tracking source ID ${sourceId}: ${existingSource} and ${source}`,
      );
    }

    sourcesById.set(sourceId, source);
  });
};

if (import.meta.env.DEV) {
  assertUniqueSourceIds();
}
